import { Accommodation } from 'src/app/trip-calculator/models/accommodation';
import {
  CarSubtype,
  FlightStopType,
  Transport,
  TransportMode,
} from 'src/app/trip-calculator/models/transport';
import { Trip } from 'src/app/trip-calculator/models/trip';
import {
  AlternativeChoice,
  AlternativeMode,
  DirectFlightTipData,
  EcoAccommodationTipData,
  ReductionTip,
  TransportAlternativeRoute,
  TransportAlternativeTipData,
} from '../models/reduction-tip';
import { getAlternative, getDefaultChoice } from './reduction-tip-savings';

/**
 * Passengers per car assumed by the carpooling alternative, also shown in its
 * label. Must match `CARPOOLING_PASSENGERS` in the API's
 * `reduction_tips_service`, or the applied trip misses the simulated figure.
 */
export const CARPOOLING_PASSENGERS = 2;

/**
 * How each alternative rewrites the transport, mirroring the mocks the API
 * priced the tip with.
 *
 * Only the three modes an estimate can offer appear: `ferry` and `mixed` exist
 * solely on a confirmed itinerary, and there the vehicle comes from the
 * itinerary itself. `carpooling` is listed all the same, because its car has to
 * carry the shared occupancy whether or not Google found the road.
 */
const ALTERNATIVE_TRANSPORT: Partial<
  Record<AlternativeMode, Partial<Transport>>
> = {
  train: { type: TransportMode.LongDistanceTrain },
  bus: { type: TransportMode.CoachBus, isPrivate: false },
  carpooling: {
    type: TransportMode.CombustionCar,
    subtype: CarSubtype.Medium,
    passengerPerVehicle: CARPOOLING_PASSENGERS,
  },
};

/**
 * One transport a confirmed itinerary becomes: a stretch ridden on one kind of
 * vehicle, from where it is boarded to where it is left.
 */
export interface AlternativeSegment {
  /** The vehicle the API priced this stretch at. */
  transportType: string;
  /** One-way kilometres of the stretch. */
  distanceKm: number;
  departureStop: string;
  arrivalStop: string;
}

/**
 * The trip as it would be once the given tips are taken, ready to be saved.
 * Pure: the trip and its collections are copied, never mutated.
 *
 * Only the quantified tips rewrite anything — `trip_duration` and
 * `high_emission_activity` are informational and are ignored here.
 *
 * `nameAlternative` renames a leg replaced by an alternative; it takes the mode
 * the leg becomes, since the name leads with it, and the segment when the
 * itinerary splits into several transports and each needs telling apart. The
 * wording is translated, which a pure function has no business reaching for.
 */
export function applyReductionTips(
  trip: Trip,
  tips: ReductionTip[],
  choices: Record<string, AlternativeChoice>,
  nameAlternative: (
    transport: Transport,
    mode: AlternativeMode,
    segment?: AlternativeSegment,
  ) => string,
): Trip {
  const transportRewrites = new Map<string, (t: Transport) => Transport[]>();
  const ecoAccommodationIds = new Set<string>();

  tips.forEach((tip) => {
    switch (tip.type) {
      case 'transport_alternative': {
        const data = tip.data as TransportAlternativeTipData;
        const choice = choices[tip.id] ?? getDefaultChoice(data);
        // A tip offering nothing is not applied rather than applied as a flight
        // replaced by a blank. The API drops such a tip, so this is a guard.
        if (!choice) break;
        transportRewrites.set(data.transportId, (transport) =>
          toAlternatives(transport, data, choice, nameAlternative),
        );
        break;
      }
      case 'direct_flight': {
        const data = tip.data as DirectFlightTipData;
        transportRewrites.set(data.transportId, (transport) => [
          toDirectFlight(transport),
        ]);
        break;
      }
      case 'eco_accommodation': {
        const data = tip.data as EcoAccommodationTipData;
        ecoAccommodationIds.add(data.accommodationId);
        break;
      }
    }
  });

  return {
    ...trip,
    // flatMap, not map: one flight can become several transports, because a
    // confirmed itinerary is priced leg by leg and has to be applied the same
    // way — see `toAlternatives`.
    transports: trip.transports.flatMap((transport) => {
      const rewrite = transport.id && transportRewrites.get(transport.id);
      return rewrite ? rewrite(transport) : [transport];
    }),
    accommodations: trip.accommodations.map((accommodation) =>
      accommodation.id && ecoAccommodationIds.has(accommodation.id)
        ? toEcoFriendly(accommodation)
        : accommodation,
    ),
  };
}

/**
 * The transports the flight becomes once the chosen itinerary is taken.
 *
 * Usually one, but a confirmed itinerary is priced leg by leg, so applying it as
 * a single transport over `route.distanceKm` would no longer add up to the
 * figure the dialog promised — a TGV continued by a FlixBus billed entirely as a
 * TGV. It becomes the transports a traveller would actually book instead.
 *
 * Consecutive legs on the same kind of vehicle are merged: same factor, so the
 * total is untouched, and the 17 km shuttle between a station and a coach stop
 * stops being a trip element of its own.
 *
 * Without an itinerary — no airports, or Google out of reach — the estimate the
 * tip was built from stands, and that is one transport over the flight's own
 * distance.
 */
function toAlternatives(
  transport: Transport,
  data: TransportAlternativeTipData,
  choice: AlternativeChoice,
  nameAlternative: (
    transport: Transport,
    mode: AlternativeMode,
    segment?: AlternativeSegment,
  ) => string,
): Transport[] {
  const { mode } = choice;
  const route = getAlternative(data, choice)?.route ?? null;
  const segments = route ? segmentsOf(route) : [];

  if (!segments.length) {
    // An estimate, or a road itinerary, which Google returns without legs.
    // `route.distanceKm` is one leg, where `data.distance` already counts both.
    const distance = route
      ? route.distanceKm * (transport.return ? 2 : 1)
      : data.distance;
    return [
      toAlternativeTransport(transport, mode, {
        title: nameAlternative(transport, mode),
        distance,
        transportType: route?.transportType,
      }),
    ];
  }

  return segments.map((segment, index) =>
    toAlternativeTransport(transport, mode, {
      title: nameAlternative(transport, mode, segment),
      distance: segment.distanceKm * (transport.return ? 2 : 1),
      transportType: segment.transportType,
      // Only the first segment takes the flight's place; the others are new
      // rows, and carrying the id would have them all overwrite it.
      isContinuation: index > 0,
    }),
  );
}

/**
 * Consecutive legs on the same kind of vehicle, as one bookable stretch.
 *
 * Access legs are left out, as the API left them out of the price — see
 * `TransportAlternativeRouteLeg.isLineHaul`.
 */
function segmentsOf(route: TransportAlternativeRoute): AlternativeSegment[] {
  return (route.legs ?? [])
    .filter((leg) => leg.isLineHaul)
    .reduce<AlternativeSegment[]>((segments, leg) => {
      const current = segments[segments.length - 1];
      if (current && current.transportType === leg.transportType) {
        current.distanceKm += leg.distanceKm;
        current.arrivalStop = leg.arrivalStop;
        return segments;
      }
      segments.push({
        transportType: leg.transportType,
        distanceKm: leg.distanceKm,
        departureStop: leg.departureStop,
        arrivalStop: leg.arrivalStop,
      });
      return segments;
    }, []);
}

/**
 * The flight rewritten as one stretch of the alternative.
 *
 * The itinerary is replaced by its distance — the API's mock does the same, and
 * for trains, buses and cars the engine reads the stops before the distance.
 * `return` is folded into that distance rather than kept as a flag.
 */
function toAlternativeTransport(
  transport: Transport,
  mode: AlternativeMode,
  segment: {
    title: string;
    distance: number;
    transportType?: string;
    isContinuation?: boolean;
  },
): Transport {
  const rewritten: Transport = {
    ...transport,
    ...ALTERNATIVE_TRANSPORT[mode],
    ...(segment.transportType
      ? { type: segment.transportType as TransportMode }
      : {}),
    // The leg no longer is what its name says — "Vol Paris → Nice" would
    // outlive the flight it describes.
    title: segment.title,
    distance: segment.distance,
    return: false,
    roadStops: [],
    flightStops: [],
    startAddress: undefined,
    endAddress: undefined,
    // The leg is no longer a flight: leaving the flight identity behind would
    // let the Travel Impact Model be consulted for a train.
    flightNumber: undefined,
    carrierCode: undefined,
    flightDate: undefined,
  };

  if (segment.isContinuation) {
    // A row that did not exist before: no id to update, and the flight's own
    // impact has nothing to say about it.
    delete rewritten.id;
    delete rewritten.transportImpactDetail;
    rewritten.isMainTransport = false;
  }

  return rewritten;
}

/** Same flight without its stopovers, departure and arrival kept in order. */
function toDirectFlight(transport: Transport): Transport {
  return {
    ...transport,
    flightStops: (transport.flightStops ?? [])
      .filter((stop) => stop.type !== FlightStopType.Stopover)
      .map((stop, index) => ({ ...stop, order: index })),
  };
}

function toEcoFriendly(accommodation: Accommodation): Accommodation {
  return { ...accommodation, isEcoFriendly: true };
}
